// Lógica de quórum y cómputo de votación para el Editor de Actas de Asamblea.
// Port de design_handoff_editor_actas/spec/voting.ts (TypeScript -> JS), manteniendo
// firmas y comportamiento como ESPECIFICACIÓN. Implementación pura, sin framework.
//
// Modelo (ver actaConfig.js):
//   edificio: { id, nombre, padron?, unidades: [{ id, etiqueta, alicuota?, titularId }] }
//   acta.asistencia: [{ unidadId, presente, votanteId?, nota? }]
//   votacion: { metodo: "unidades" | "coeficiente", votosPorPersona: { favor:[], contra:[], abstencion:[] } }

function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

/**
 * Mapa unidadId -> alícuota efectiva en PORCENTAJE (base 100), calculado una vez
 * por edificio.
 *
 * - Si alguna unidad tiene `alicuota`, se NORMALIZA el conjunto para que la suma
 *   total sea 100%. Esto tolera que el dato venga en milésimas (suma 1000),
 *   fracciones (suma 1) o ya en porcentaje (suma 100): en todos los casos el
 *   coeficiente resultante y el quórum quedan sobre base 100.
 * - Si NINGUNA unidad tiene alícuota, se reparte 100% en partes iguales.
 */
export function tablaAlicuotas(edificio) {
  const unidades = edificio?.unidades || [];
  const t = {};
  if (unidades.length === 0) return t;

  const algunaConAlicuota = unidades.some(
    (u) => typeof u.alicuota === "number" && !Number.isNaN(u.alicuota)
  );

  if (!algunaConAlicuota) {
    const each = 100 / unidades.length; // reparto equitativo (ya suma 100)
    for (const u of unidades) t[u.id] = each;
    return t;
  }

  // Normalizar a base 100.
  const raw = {};
  let total = 0;
  for (const u of unidades) {
    const v = typeof u.alicuota === "number" && !Number.isNaN(u.alicuota) ? u.alicuota : 0;
    raw[u.id] = v;
    total += v;
  }
  if (total <= 0) {
    const each = 100 / unidades.length;
    for (const u of unidades) t[u.id] = each;
    return t;
  }
  for (const u of unidades) t[u.id] = (raw[u.id] / total) * 100;
  return t;
}

/**
 * Alícuota efectiva (en %, base 100) de una unidad. Delega en la tabla
 * normalizada para que el resultado siempre esté sobre 100.
 */
export function resolverAlicuota(unidad, edificio) {
  const t = tablaAlicuotas(edificio);
  return t[unidad?.id] ?? 0;
}

/**
 * Unidades que una persona "carga" en esta acta = todas las unidades presentes
 * que la tienen como votante (su titularidad presente + poderes recibidos).
 */
export function unidadesDePersona(personaId, acta) {
  return (acta?.asistencia || [])
    .filter((a) => a.presente && String(a.votanteId) === String(personaId))
    .map((a) => a.unidadId);
}

/**
 * QUÓRUM por coeficiente.
 * coeficientePresente = suma de alícuotas de las unidades presentes.
 * `umbralHabilitante` por defecto 50%. Para 2ª convocatoria suele ser 0
 * (se sesiona con los presentes) — pasar el umbral que corresponda.
 */
export function calcularQuorum(acta, edificio, umbralHabilitante = 50) {
  const alic = tablaAlicuotas(edificio);
  const presentes = (acta?.asistencia || []).filter((a) => a.presente);
  const coeficientePresente = presentes.reduce((s, a) => s + (alic[a.unidadId] ?? 0), 0);
  return {
    unidadesPresentes: presentes.length,
    unidadesTotales: (edificio?.unidades || []).length,
    coeficientePresente: round2(coeficientePresente),
    habilitante: coeficientePresente >= umbralHabilitante,
  };
}

/**
 * CÓMPUTO de una votación.
 * Para cada sentido, expande las personas elegidas a sus unidades presentes y suma:
 *   - metodo "unidades"    -> cantidad de unidades
 *   - metodo "coeficiente" -> suma de alícuotas (%)
 * Una unidad presente no asignada a ningún sentido NO suma (se considera no emitida).
 *
 * Umbral: "mayoria" aprueba con favor > 50% del total computado; "unanime" exige
 * contra=0 y abstencion=0 (y favor>0). "informativa" no se aprueba/rechaza.
 */
export function calcularVotacion(votacion, acta, edificio, tipoResolucion = "mayoria") {
  const alic = tablaAlicuotas(edificio);
  const metodo = votacion?.metodo === "coeficiente" ? "coeficiente" : "unidades";
  const votosPorPersona = votacion?.votosPorPersona || {};

  const pesoDeUnidad = (unidadId) => (metodo === "coeficiente" ? (alic[unidadId] ?? 0) : 1);

  const sumarSentido = (sentido) => {
    const personas = votosPorPersona[sentido] || [];
    const unidades = new Set();
    for (const pid of personas) for (const uid of unidadesDePersona(pid, acta)) unidades.add(uid);
    return [...unidades].reduce((s, uid) => s + pesoDeUnidad(uid), 0);
  };

  const favor = round2(sumarSentido("favor"));
  const contra = round2(sumarSentido("contra"));
  const abstencion = round2(sumarSentido("abstencion"));
  const totalComputado = round2(favor + contra + abstencion);

  let aprobado;
  if (tipoResolucion === "unanime") aprobado = contra === 0 && abstencion === 0 && favor > 0;
  else if (tipoResolucion === "informativa") aprobado = false;
  else aprobado = favor > totalComputado / 2;

  return { metodo, favor, contra, abstencion, totalComputado, aprobado };
}

/**
 * Validación: una persona no puede estar en dos sentidos a la vez.
 * Devuelve { valido, duplicados: [personaId] }.
 */
export function validarVotacion(votacion) {
  const votos = votacion?.votosPorPersona || {};
  const seen = new Map();
  const duplicados = new Set();
  for (const sentido of ["favor", "contra", "abstencion"]) {
    for (const pid of votos[sentido] || []) {
      const key = String(pid);
      if (seen.has(key) && seen.get(key) !== sentido) duplicados.add(key);
      seen.set(key, sentido);
    }
  }
  return { valido: duplicados.size === 0, duplicados: [...duplicados] };
}

/** Lista de copropietarios presentes (ids únicos de votantes presentes). */
export function personasPresentes(acta) {
  const ids = new Set();
  for (const a of acta?.asistencia || []) {
    if (a.presente && a.votanteId != null && a.votanteId !== "") ids.add(String(a.votanteId));
  }
  return [...ids];
}
